/**
 * WP4c / #46609 — verification-evidence contracts for Evidence artifacts/
 * lineage and Connector catalog/health/credentials.
 *
 * Session-authenticated product UI API (`apiCall` / `useApi`). Distinct from
 * the SKU-A task-evidence door at `/api/evidence/**` and from the
 * token-scoped public catalog at `/api/verification/v1`.
 *
 * Environment: every call carries `env` (query) and/or
 * `x-excellent-verification-environment`. Object ids are namespaced
 * `demo:` / `real:` (WP2). Handlers must call `enforceVerificationEnvironment`.
 *
 * HG11 (#46595): mutations use existing ROLE_CAPABILITIES — no new
 * capabilities. Artifact redact + queue-collection require `evidence:write`
 * (admin/owner; owner escape is `verification:*`). Connector reconnect
 * requires `connector:write`. Reads are ungated at the Hono seam, same as
 * WP4a overview.
 *
 * WP4a (overview/queue/findings/runs) and WP4b (workflows/controls/policies)
 * are out of scope — do not edit `verification-shell.ts` or
 * `verification-define.ts`. Demo seed/reset is WP3. Schema is WP4g.
 */

import { z } from "zod";
import type { Args, Operation } from "../types";
import { scoped } from "../types";
import { keysetQuery, keysetSchema } from "./_common";

export const VERIFICATION_EVIDENCE_SCHEMA_VERSION = 1 as const;

const envName = z.enum(["demo", "real"]);
export const verificationEvidenceEnvQuery = z.object({
  env: envName.optional(),
  actorId: z.string().min(1).max(200).optional(),
});
const namespacedId = z.string().min(1).max(200);
const iso = z.string().min(1);
const artifactState = z.enum(["active", "redacted", "deleted", "expired"]);
const sourceHealth = z.enum(["healthy", "stale", "blocked", "revoked", "paused"]);
const connectorProvider = z.enum(["okta", "workday", "github", "aws-iam", "jira", "vanta"]);
const connectorFamily = z.enum(["idp", "hris", "cloud", "scm", "ticketing", "grc", "manual"]);
const lastRunStatus = z.enum([
  "succeeded",
  "failed",
  "retrying",
  "running",
  "revoked",
  "never-run",
]);
const lineageKind = z.enum(["collected", "derived", "redacted", "deleted", "retention_expired"]);
const credentialAuthMode = z.enum(["oauth2", "api-token", "service-account"]);
const credentialSecretRefKind = z.enum(["excellent-vault", "customer-kms", "local-keychain"]);
const credentialHealth = z.enum(["usable", "expiring", "blocked"]);
const sourceIntentDecision = z.enum(["bounded", "abstain", "conflict"]);
const sourceIntentSourceKind = z.enum([
  "organization_policy",
  "repository_policy",
  "requester_work_object",
  "trusted_workflow_template",
  "observed_occurrence",
  "worker_proposal",
]);
const sourceIntentPermissionTier = z.enum([
  "presence",
  "aggregate",
  "redacted_structure",
  "content",
  "verification",
]);
const sourceIntentScenario = z.enum([
  "ambiguous-production-ready",
  "concrete-retry-resume",
  "conflicting-policy",
  "revoked-grant",
  "workflow-template-observed-occurrence",
]);
const connectorCapability = z.enum([
  "population",
  "evidence_snapshot",
  "approval_status",
  "access_state",
  "ticket_state",
  "control_mapping",
]);
const capabilityRegistryManagementAction = z.enum([
  "install_oracle",
  "delegate_oracle",
  "disable_oracle",
]);
const capabilityRegistryAdapterState = z.enum(["installed", "delegated", "disabled"]);

const provenance = z.object({
  environment: envName,
  demoScoped: z.boolean(),
  trustChain: envName,
});

export type VerificationEvidenceEnvQuery = z.infer<typeof verificationEvidenceEnvQuery>;

export const verificationEvidenceArtifactDto = z.object({
  id: namespacedId,
  sourceId: namespacedId,
  sourceKind: z.string().min(1),
  label: z.string().min(1),
  contentHash: z.string().min(8),
  collectedAt: iso,
  collectorActorId: namespacedId,
  state: artifactState,
  encryptedAtRest: z.literal(true),
  sizeBytes: z.number().int().nonnegative(),
  observationCount: z.number().int().nonnegative(),
  controlIds: z.array(namespacedId),
  citedByReceiptIds: z.array(namespacedId),
  collectionQueuedAt: iso.nullable(),
  redactedAt: iso.nullable(),
});
export type VerificationEvidenceArtifactDto = z.infer<typeof verificationEvidenceArtifactDto>;

export const verificationEvidenceLineageEventDto = z.object({
  id: namespacedId,
  kind: lineageKind,
  actorId: namespacedId,
  occurredAt: iso,
  contentHash: z.string().min(8),
  prevEventHash: z.string().min(8).nullable(),
  eventHash: z.string().min(8),
  reason: z.string().min(1),
});
export type VerificationEvidenceLineageEventDto = z.infer<
  typeof verificationEvidenceLineageEventDto
>;

export const verificationConnectorDto = z.object({
  id: namespacedId,
  provider: connectorProvider,
  family: connectorFamily,
  displayName: z.string().min(1),
  health: sourceHealth,
  readOnly: z.literal(true),
  grantedScopes: z.array(z.string().min(1)),
  requiredScopes: z.array(z.string().min(1)),
  cadenceMinutes: z.number().int().positive(),
  maxFreshnessMinutes: z.number().int().positive(),
  lastSuccessfulRunAt: iso.nullable(),
  nextRunAt: iso.nullable(),
  lastRunStatus,
  artifactCount: z.number().int().nonnegative(),
  controlIds: z.array(namespacedId),
  note: z.string().min(1).nullable(),
});
export type VerificationConnectorDto = z.infer<typeof verificationConnectorDto>;

export const verificationConnectorCatalogItemDto = z.object({
  provider: connectorProvider,
  family: connectorFamily,
  displayName: z.string().min(1),
  readOnly: z.literal(true),
  requiredScopes: z.array(z.string().min(1)),
  capabilities: z.array(connectorCapability).min(1),
});
export type VerificationConnectorCatalogItemDto = z.infer<
  typeof verificationConnectorCatalogItemDto
>;

export const verificationCapabilityRegistryManagementDto = z.object({
  id: namespacedId,
  provider: connectorProvider,
  family: connectorFamily,
  oracleAdapter: z.string().min(1),
  state: capabilityRegistryAdapterState,
  actorId: namespacedId,
  managedAt: iso,
  providerCapabilities: z.array(connectorCapability).min(1),
  requiredScopes: z.array(z.string().min(1)),
  routingOnly: z.literal(true),
  verdictStatus: z.literal("NOT_RUN"),
  plannedGap: z.string().min(1).nullable(),
});
export type VerificationCapabilityRegistryManagementDto = z.infer<
  typeof verificationCapabilityRegistryManagementDto
>;

export const verificationConnectorCredentialDto = z.object({
  connectionId: namespacedId,
  provider: connectorProvider,
  authMode: credentialAuthMode,
  secretRefKind: credentialSecretRefKind,
  secretRefPresent: z.boolean(),
  grantedScopes: z.array(z.string().min(1)),
  issuedAt: iso,
  expiresAt: iso.nullable(),
  lastRotatedAt: iso,
  health: credentialHealth,
  revokedAt: iso.nullable(),
});
export type VerificationConnectorCredentialDto = z.infer<typeof verificationConnectorCredentialDto>;

const idParams = z.object({ id: namespacedId });
export type VerificationEvidenceIdParams = z.infer<typeof idParams>;

const artifactsListQuery = verificationEvidenceEnvQuery.extend({
  state: artifactState.optional(),
  sourceId: namespacedId.optional(),
});
export type VerificationEvidenceArtifactsListQuery = z.infer<typeof artifactsListQuery>;

export const verificationEvidenceArtifactsListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationEvidenceArtifactDto),
});
export type VerificationEvidenceArtifactsListResponse = z.infer<
  typeof verificationEvidenceArtifactsListResponse
>;

export const verificationEvidenceArtifactGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  artifact: verificationEvidenceArtifactDto,
});
export type VerificationEvidenceArtifactGetResponse = z.infer<
  typeof verificationEvidenceArtifactGetResponse
>;

export const verificationEvidenceLineageGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  artifactId: namespacedId,
  contentHash: z.string().min(8),
  terminalState: artifactState,
  headEventHash: z.string().min(8),
  entries: z.array(verificationEvidenceLineageEventDto).min(1),
});
export type VerificationEvidenceLineageGetResponse = z.infer<
  typeof verificationEvidenceLineageGetResponse
>;

export const verificationEvidenceRedactBody = z.object({
  actorId: namespacedId,
  rationale: z.string().min(1),
});
export type VerificationEvidenceRedactBody = z.infer<typeof verificationEvidenceRedactBody>;

export const verificationEvidenceRedactResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  artifact: verificationEvidenceArtifactDto,
});
export type VerificationEvidenceRedactResponse = z.infer<typeof verificationEvidenceRedactResponse>;

export const verificationEvidenceQueueBody = z.object({
  actorId: namespacedId,
});
export type VerificationEvidenceQueueBody = z.infer<typeof verificationEvidenceQueueBody>;

export const verificationEvidenceQueueResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  artifact: verificationEvidenceArtifactDto,
  sourceId: namespacedId,
});
export type VerificationEvidenceQueueResponse = z.infer<typeof verificationEvidenceQueueResponse>;

const connectorsListQuery = verificationEvidenceEnvQuery.extend({
  health: sourceHealth.optional(),
  family: connectorFamily.optional(),
});
export type VerificationConnectorsListQuery = z.infer<typeof connectorsListQuery>;

export const verificationConnectorsListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationConnectorDto),
});
export type VerificationConnectorsListResponse = z.infer<typeof verificationConnectorsListResponse>;

export const verificationConnectorsCatalogResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationConnectorCatalogItemDto),
});
export type VerificationConnectorsCatalogResponse = z.infer<
  typeof verificationConnectorsCatalogResponse
>;

export const verificationCapabilityRegistryManagementListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationCapabilityRegistryManagementDto),
  installedAdapters: z.array(z.string().min(1)),
  delegatedAdapters: z.array(z.string().min(1)),
  disabledAdapters: z.array(z.string().min(1)),
  routingOnly: z.literal(true),
  verdictStatus: z.literal("NOT_RUN"),
});
export type VerificationCapabilityRegistryManagementListResponse = z.infer<
  typeof verificationCapabilityRegistryManagementListResponse
>;

export const verificationConnectorGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  connector: verificationConnectorDto,
});
export type VerificationConnectorGetResponse = z.infer<typeof verificationConnectorGetResponse>;

export const verificationConnectorCredentialsGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  credential: verificationConnectorCredentialDto,
});
export type VerificationConnectorCredentialsGetResponse = z.infer<
  typeof verificationConnectorCredentialsGetResponse
>;

export const sourceIntentInferenceSourceDto = z.object({
  id: namespacedId,
  kind: sourceIntentSourceKind,
  digest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  span: z.string().min(1),
  text: z.string().min(1).nullable(),
  permissionTier: sourceIntentPermissionTier,
  revoked: z.boolean().optional(),
});
export const sourceIntentIntakeSourceDto = sourceIntentInferenceSourceDto.extend({
  text: z.string().min(1),
});
export const sourceIntentIntakeBody = z.object({
  scenario: sourceIntentScenario,
  sources: z.array(sourceIntentIntakeSourceDto.extend({ expectedGrantRevision: namespacedId.optional() })).min(2).max(100),
});
export type SourceIntentIntakeBody = z.infer<typeof sourceIntentIntakeBody>;
// Owners can revoke or replace one source without resubmitting unrelated content.
export const sourceIntentGrantBody = z.object({
  scenario: sourceIntentScenario,
  sources: z.array(sourceIntentIntakeSourceDto).min(1).max(100),
});
export type SourceIntentGrantBody = z.infer<typeof sourceIntentGrantBody>;
export const sourceIntentGrantResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  scenario: sourceIntentScenario,
  grantedToActorId: namespacedId,
  grantedByActorId: namespacedId,
  grantedSourceIds: z.array(namespacedId).min(1),
  grantedAt: iso,
});
export type SourceIntentGrantResponse = z.infer<typeof sourceIntentGrantResponse>;
export const sourceIntentInferenceResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  engineVersion: z.string().min(1),
  scenario: sourceIntentScenario,
  evidenceOrigin: z.enum(["synthetic_scenario", "session_demo_intake", "product_source_intake"]),
  // Product snapshots are selectable by exact ID; examples have no persisted intake.
  intakeId: z.string().min(1).max(200).optional(),
  decision: sourceIntentDecision,
  sourcePrecedence: z.array(sourceIntentSourceKind).min(1),
  // Every pinned grant may have become unavailable since the recorded intake.
  sources: z.array(sourceIntentInferenceSourceDto),
  outcomes: z.array(
    z.object({
      id: namespacedId,
      statement: z.string().min(1),
      citedSourceIds: z.array(namespacedId),
      omittedSourceIds: z.array(namespacedId),
      confidence: z.enum(["source-backed", "bounded", "abstained"]),
    }),
  ),
  alternatives: z.array(
    z.object({
      id: namespacedId,
      statement: z.string().min(1),
      citedSourceIds: z.array(namespacedId),
    }),
  ),
  contradictions: z.array(
    z.object({
      sourceIds: z.array(namespacedId).min(1),
      summary: z.string().min(1),
    }),
  ),
  omissions: z.array(
    z.object({
      sourceId: namespacedId,
      reason: z.enum(["permission_denied", "revoked", "insufficient_content"]),
    }),
  ),
  permissionLimit: z.string().min(1).nullable(),
  observationAllowed: z.boolean(),
  retainedArtifactPolicy: z.string().min(1),
});
export type SourceIntentInferenceResponse = z.infer<typeof sourceIntentInferenceResponse>;
export const sourceIntentCompileBody = z
  .object({
    scenario: sourceIntentScenario,
    bindingKind: z.literal("host-checks").optional(),
    intakeId: z.string().min(1).max(200),
    requestId: namespacedId.max(200).optional(),
  })
  .strict();
export type SourceIntentCompileBody = z.infer<typeof sourceIntentCompileBody>;
const sourceIntentCompileCancelBody = z.object({ scenario: sourceIntentScenario, requestId: namespacedId.max(200) }).strict();
export type SourceIntentCompileCancelBody = z.infer<typeof sourceIntentCompileCancelBody>;
export const sourceIntentCompileStatusQuery = verificationEvidenceEnvQuery.extend({
  scenario: sourceIntentScenario, intakeId: namespacedId.max(200), requestId: namespacedId.max(200).optional(),
});
export type SourceIntentCompileStatusQuery = z.infer<typeof sourceIntentCompileStatusQuery>;
export const sourceIntentCompileStatusResponse = z.object({
  state: z.enum(["none", "running", "succeeded", "failed", "cancelled", "interrupted", "stale", "expired"]),
  intakeId: namespacedId, requestId: namespacedId.nullable(), compilationId: namespacedId.nullable(),
  leaseUntil: z.string().datetime().nullable(), updatedAt: z.string().datetime().nullable(),
}).strict();
const sourceIntentSessionResponse = z.object({
  actorId: namespacedId, tenantId: namespacedId, compilerEnabled: z.boolean(), canReview: z.boolean(),
});
const sourceCompilerMetricScope = z.object({
  repositoryId: namespacedId, filePath: z.string().min(1).max(1024), pointer: z.string().max(1024),
  operation: z.enum(["array_length", "number"]), unit: z.string().min(1).max(64),
}).strict();
const sourceCompilerMetricChoice = z.object({
  predicateId: namespacedId, scope: sourceCompilerMetricScope,
  environmentDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/), maximumAgeSeconds: z.number().int().positive().max(86400),
}).strict();
export const sourceIntentReviewBody = z.object({
  scenario: sourceIntentScenario, compilationId: namespacedId,
  expectedCompilationDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  expectedReviewDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/).nullable(),
  idempotencyKey: namespacedId, selections: z.array(sourceCompilerMetricChoice).max(32),
  bindingKind: z.literal("host-checks").optional(),
  contextDecisions: z.array(z.object({ clauseId: namespacedId,
    disposition: z.enum(["context", "requirement"]) }).strict()).max(128).optional(),
}).strict();
export type SourceIntentReviewBody = z.infer<typeof sourceIntentReviewBody>;
// Review-card projection only. Canonical ContractObligations remain server-owned;
// this DTO cannot be submitted to accept a Contract or manufacture admission.
export const sourceIntentReviewResponse = z.object({
  reviewId: namespacedId, reviewDigest: z.string(), compilationId: namespacedId, compilationDigest: z.string(),
  revision: z.number().int().positive(), previousReviewDigest: z.string().nullable(), createdAt: iso, expiresAt: iso,
  current: z.boolean(), currentReviewId: namespacedId, currentReviewDigest: z.string(),
  sourceSnapshotDigest: z.string(), compilerResultDigest: z.string(), status: z.enum(["proposal", "needs_attention"]),
  selections: z.array(sourceCompilerMetricChoice),
  contextDecisions: z.array(z.object({ clauseId: namespacedId, disposition: z.enum(["context", "requirement"]) })).optional(),
  checks: z.array(z.object({ id: namespacedId, sourceIds: z.array(namespacedId), statement: z.string(),
    kind: z.enum(["artifact_present", "resource_bounds", "json_equals", "exit_code", "git_json_numeric_compare"]).optional(), command: z.string().optional(),
    comparator: z.enum(["lt", "lte", "eq", "gte", "gt"]).optional(), bound: z.number().finite().optional(), scope: sourceCompilerMetricScope.optional(),
    environmentDigest: z.string().optional(), maximumAgeSeconds: z.number().int().positive(), evaluatorRef: z.string(), evaluatorVersion: z.string(), evaluatorDigest: z.string(),
  }).superRefine((check, ctx) => {
    if (["artifact_present", "resource_bounds", "json_equals", "exit_code"].includes(check.kind ?? "")) {
      if (check.kind === "exit_code" && !check.command?.trim())
        ctx.addIssue({ code: "custom", message: "behavioral-command-required" });
    } else if (!check.comparator || check.bound === undefined || !check.scope || !check.environmentDigest) {
      ctx.addIssue({ code: "custom", message: "typed-measurement-fields-required" });
    }
  })),
  gaps: z.array(z.object({ clauseId: z.string().nullable(), reason: z.string() })),
  excludedClauses: z.array(z.object({ clauseId: z.string(), sourceSpan: z.string(), reason: z.string() })),
  persisted: z.literal(true), accepted: z.literal(false), reviewRequired: z.literal(true),
  admissionRequired: z.literal(true), scopeAuthorization: z.literal("not_checked"),
});
// Wire projection of the canonical compiler's predicate, not a new Contract.
const compiledSourcePredicate = z.object({
  id: z.string(),
  kind: z.enum(["checkpoint", "band", "external-oracle", "ordering", "row"]),
  tier: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  signature: z.object({
    symbols: z.array(
      z.object({
        name: z.string(),
        sort: z.enum(["Bool", "Int", "Real", "String"]),
        unit: z.string().nullable(),
        meaning: z.string(),
        span: z.string(),
      }),
    ),
  }),
  expr: z.string(),
  falsifier: z.string(),
  source_span: z.string(),
});
export const sourceIntentCompileResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  intakeId: z.string(),
  sourceSnapshotDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  status: z.enum(["proposal", "needs_attention"]),
  accepted: z.literal(false),
  persisted: z.literal(true),
  compilationId: z.string().min(1).max(200),
  compilationDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  createdAt: iso,
  expiresAt: iso,
  reviewRequired: z.literal(true),
  sources: z.array(
    z.object({
      sourceId: z.string(),
      kind: sourceIntentSourceKind,
      digest: z.string(),
      grantRevision: z.string().nullable(),
      span: z.string(),
      exclusionReason: z.string().nullable(),
    }),
  ),
  model: z.string(),
  // Optional only for immutable historical results; absence is never attestation.
  modelIdentity: z.object({
    schema: z.literal("excellent.source-compiler.runtime-identity.v1"),
    basis: z.literal("runtime-reported-pre-post"),
    executedWeightsAttested: z.literal(false),
    provider: z.literal("ollama"), model: z.string(), endpoint: z.string().url(),
    expectedDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
    before: z.object({ observedAt: iso, inventoryDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/), residentDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/).nullable() }).strict(),
    after: z.object({ observedAt: iso, inventoryDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/), residentDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/).nullable() }).strict(),
  }).strict().optional(),
  attempts: z.number(),
  predicates: z.array(compiledSourcePredicate),
  rejected: z.array(z.object({ draft: z.string(), reason: z.string() })),
  clauses: z.array(
    z.object({
      id: z.string(),
      sourceId: z.string(),
      sourceStart: z.number().int().nonnegative(),
      sourceEnd: z.number().int().nonnegative(),
      sourceSpan: z.string(),
      kind: z.enum(["numeric", "ordering", "assertion", "clarify", "context"]).nullable(),
      reason: z.string().nullable(),
      candidate: compiledSourcePredicate.nullable(),
    }),
  ),
});
export type SourceIntentScenario = z.infer<typeof sourceIntentScenario>;

export const verificationConnectorReconnectBody = z.object({
  actorId: namespacedId,
});
export type VerificationConnectorReconnectBody = z.infer<typeof verificationConnectorReconnectBody>;

export const verificationConnectorReconnectResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  connector: verificationConnectorDto,
  credential: verificationConnectorCredentialDto,
});
export type VerificationConnectorReconnectResponse = z.infer<
  typeof verificationConnectorReconnectResponse
>;

export const verificationCapabilityRegistryManageBody = z.object({
  actorId: namespacedId,
  provider: connectorProvider,
  action: capabilityRegistryManagementAction,
  oracleAdapter: z.string().min(1).optional(),
});
export type VerificationCapabilityRegistryManageBody = z.infer<
  typeof verificationCapabilityRegistryManageBody
>;

export const verificationCapabilityRegistryManageResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_EVIDENCE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  management: verificationCapabilityRegistryManagementDto,
  installedAdapters: z.array(z.string().min(1)),
  delegatedAdapters: z.array(z.string().min(1)),
  disabledAdapters: z.array(z.string().min(1)),
  routingOnly: z.literal(true),
  verdictStatus: z.literal("NOT_RUN"),
});
export type VerificationCapabilityRegistryManageResponse = z.infer<
  typeof verificationCapabilityRegistryManageResponse
>;

const artifactsListOp: Operation<
  void,
  VerificationEvidenceArtifactsListQuery,
  void,
  VerificationEvidenceArtifactsListResponse
> = {
  method: "GET",
  path: () => "/api/verification/evidence",
  query: artifactsListQuery,
  response: verificationEvidenceArtifactsListResponse,
};

const artifactsGetOp: Operation<
  VerificationEvidenceIdParams,
  VerificationEvidenceEnvQuery,
  void,
  VerificationEvidenceArtifactGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/evidence/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationEvidenceEnvQuery,
  response: verificationEvidenceArtifactGetResponse,
};

const lineageGetOp: Operation<
  VerificationEvidenceIdParams,
  VerificationEvidenceEnvQuery,
  void,
  VerificationEvidenceLineageGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/evidence/${encodeURIComponent(id)}/lineage`,
  params: idParams,
  query: verificationEvidenceEnvQuery,
  response: verificationEvidenceLineageGetResponse,
};

const artifactsRedactOp: Operation<
  VerificationEvidenceIdParams,
  VerificationEvidenceEnvQuery,
  VerificationEvidenceRedactBody,
  VerificationEvidenceRedactResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/evidence/${encodeURIComponent(id)}/redact`,
  params: idParams,
  query: verificationEvidenceEnvQuery,
  body: verificationEvidenceRedactBody,
  response: verificationEvidenceRedactResponse,
  invalidates: [artifactsListOp],
};

const artifactsQueueOp: Operation<
  VerificationEvidenceIdParams,
  VerificationEvidenceEnvQuery,
  VerificationEvidenceQueueBody,
  VerificationEvidenceQueueResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/evidence/${encodeURIComponent(id)}/queue-collection`,
  params: idParams,
  query: verificationEvidenceEnvQuery,
  body: verificationEvidenceQueueBody,
  response: verificationEvidenceQueueResponse,
  invalidates: [artifactsListOp],
};

const connectorsListOp: Operation<
  void,
  VerificationConnectorsListQuery,
  void,
  VerificationConnectorsListResponse
> = {
  method: "GET",
  path: () => "/api/verification/connectors",
  query: connectorsListQuery,
  response: verificationConnectorsListResponse,
};

const connectorsCatalogOp: Operation<
  void,
  VerificationEvidenceEnvQuery,
  void,
  VerificationConnectorsCatalogResponse
> = {
  method: "GET",
  path: () => "/api/verification/connectors/catalog",
  query: verificationEvidenceEnvQuery,
  response: verificationConnectorsCatalogResponse,
};

const capabilityRegistryManagementListOp: Operation<
  void,
  VerificationEvidenceEnvQuery,
  void,
  VerificationCapabilityRegistryManagementListResponse
> = {
  method: "GET",
  path: () => "/api/verification/capability-registry/management",
  query: verificationEvidenceEnvQuery,
  response: verificationCapabilityRegistryManagementListResponse,
};

const sourceIntentGetQuery = verificationEvidenceEnvQuery.extend({
  scenario: sourceIntentScenario.optional(),
  intakeId: z.string().min(1).max(200).optional(),
});
const sourceIntentSessionOp: Operation<void, void, void, z.infer<typeof sourceIntentSessionResponse>> = {
  method: "GET", path: () => "/api/verification/source-intent/session", response: sourceIntentSessionResponse,
};
const sourceIntentLibraryQuery = verificationEvidenceEnvQuery.merge(keysetQuery).extend({
  scenario: sourceIntentScenario,
  cursor: z.string().min(1).max(4096).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type SourceIntentLibraryQuery = z.infer<typeof sourceIntentLibraryQuery>;
const sourceIntentLibraryResponse = keysetSchema(z.object({
  intakeId: namespacedId, createdAt: iso, sourceCount: z.number().int().nonnegative().nullable(),
  sourceAuthority: z.literal("recheck_required"),
  compilation: z.object({
    compilationId: namespacedId, createdAt: iso, expiresAt: iso,
    state: z.enum(["saved", "expired", "invalidated"]),
    reviewId: namespacedId.nullable(), reviewRevision: z.number().int().positive().nullable(),
  }).nullable(),
}));
const sourceIntentLibraryOp: Operation<void, SourceIntentLibraryQuery, void, z.infer<typeof sourceIntentLibraryResponse>> = {
  method: "GET", path: () => "/api/verification/source-intent/library",
  query: sourceIntentLibraryQuery, response: sourceIntentLibraryResponse,
};
const sourceIntentSourcesQuery = sourceIntentLibraryQuery;
export type SourceIntentSourcesQuery = z.infer<typeof sourceIntentSourcesQuery>;
export const sourceIntentGrantedSourceDto = z.object({
  source: sourceIntentInferenceSourceDto, grantRevision: namespacedId,
});
export type SourceIntentGrantedSource = z.infer<typeof sourceIntentGrantedSourceDto>;
const sourceIntentSourcesResponse = keysetSchema(sourceIntentGrantedSourceDto);
const sourceIntentSourcesOp: Operation<void, SourceIntentSourcesQuery, void,
  z.infer<typeof sourceIntentSourcesResponse>> = {
  method: "GET", path: () => "/api/verification/source-intent/sources",
  query: sourceIntentSourcesQuery, response: sourceIntentSourcesResponse,
};
export type SourceIntentGetQuery = z.infer<typeof sourceIntentGetQuery>;

const sourceIntentGetOp: Operation<
  void,
  SourceIntentGetQuery,
  void,
  SourceIntentInferenceResponse
> = {
  method: "GET",
  path: () => "/api/verification/source-intent",
  query: sourceIntentGetQuery,
  response: sourceIntentInferenceResponse,
};

const sourceIntentIntakeOp: Operation<
  void,
  VerificationEvidenceEnvQuery,
  SourceIntentIntakeBody,
  SourceIntentInferenceResponse
> = {
  method: "POST",
  path: () => "/api/verification/source-intent/intake",
  query: verificationEvidenceEnvQuery,
  body: sourceIntentIntakeBody,
  response: sourceIntentInferenceResponse,
  invalidates: [sourceIntentGetOp, sourceIntentLibraryOp],
};

const sourceIntentCompileOp: Operation<
  void,
  VerificationEvidenceEnvQuery,
  SourceIntentCompileBody,
  z.infer<typeof sourceIntentCompileResponse>
> = {
  method: "POST",
  path: () => "/api/verification/source-intent/compile",
  query: verificationEvidenceEnvQuery,
  body: sourceIntentCompileBody,
  response: sourceIntentCompileResponse,
  invalidates: [sourceIntentLibraryOp],
};

const sourceIntentCompilationGetOp: Operation<
  void,
  VerificationEvidenceEnvQuery & { scenario: SourceIntentScenario; compilationId: string },
  void,
  z.infer<typeof sourceIntentCompileResponse>
> = {
  method: "GET",
  path: () => "/api/verification/source-intent/compilations",
  query: verificationEvidenceEnvQuery.extend({ scenario: sourceIntentScenario, compilationId: namespacedId }),
  response: sourceIntentCompileResponse,
};

const sourceIntentReviewOp: Operation<void, VerificationEvidenceEnvQuery, SourceIntentReviewBody, z.infer<typeof sourceIntentReviewResponse>> = {
  method: "POST", path: () => "/api/verification/source-intent/reviews", query: verificationEvidenceEnvQuery,
  body: sourceIntentReviewBody, response: sourceIntentReviewResponse,
};
const sourceIntentReviewGetOp: Operation<void, VerificationEvidenceEnvQuery & { scenario: SourceIntentScenario; reviewId: string }, void, z.infer<typeof sourceIntentReviewResponse>> = {
  method: "GET", path: () => "/api/verification/source-intent/reviews",
  query: verificationEvidenceEnvQuery.extend({ scenario: sourceIntentScenario, reviewId: namespacedId }), response: sourceIntentReviewResponse,
};
sourceIntentReviewOp.invalidates = [sourceIntentReviewGetOp, sourceIntentLibraryOp];
const sourceIntentCompileCancelOp: Operation<void, VerificationEvidenceEnvQuery, SourceIntentCompileCancelBody,
  { state: "cancellation_requested" | "not_active" }> = {
  method: "POST", path: () => "/api/verification/source-intent/compile/cancel",
  query: verificationEvidenceEnvQuery, body: sourceIntentCompileCancelBody,
  response: z.object({ state: z.enum(["cancellation_requested", "not_active"]) }),
};
const sourceIntentCompileStatusOp: Operation<void, SourceIntentCompileStatusQuery, void,
  z.infer<typeof sourceIntentCompileStatusResponse>> = {
  method: "GET", path: () => "/api/verification/source-intent/compile/status",
  query: sourceIntentCompileStatusQuery, response: sourceIntentCompileStatusResponse,
};

const sourceIntentGrantOp: Operation<
  void,
  VerificationEvidenceEnvQuery,
  SourceIntentGrantBody,
  SourceIntentGrantResponse
> = {
  method: "POST",
  path: () => "/api/verification/source-intent/grants",
  query: verificationEvidenceEnvQuery,
  body: sourceIntentGrantBody,
  response: sourceIntentGrantResponse,
  invalidates: [sourceIntentGetOp, sourceIntentCompilationGetOp, sourceIntentReviewGetOp, sourceIntentLibraryOp, sourceIntentSourcesOp],
};

const connectorsGetOp: Operation<
  VerificationEvidenceIdParams,
  VerificationEvidenceEnvQuery,
  void,
  VerificationConnectorGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/connectors/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationEvidenceEnvQuery,
  response: verificationConnectorGetResponse,
};

const credentialsGetOp: Operation<
  VerificationEvidenceIdParams,
  VerificationEvidenceEnvQuery,
  void,
  VerificationConnectorCredentialsGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/connectors/${encodeURIComponent(id)}/credentials`,
  params: idParams,
  query: verificationEvidenceEnvQuery,
  response: verificationConnectorCredentialsGetResponse,
};

const connectorsReconnectOp: Operation<
  VerificationEvidenceIdParams,
  VerificationEvidenceEnvQuery,
  VerificationConnectorReconnectBody,
  VerificationConnectorReconnectResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/connectors/${encodeURIComponent(id)}/reconnect`,
  params: idParams,
  query: verificationEvidenceEnvQuery,
  body: verificationConnectorReconnectBody,
  response: verificationConnectorReconnectResponse,
  invalidates: [
    connectorsListOp,
    scoped(credentialsGetOp, (args: Args<VerificationEvidenceIdParams, unknown, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

const capabilityRegistryManageOp: Operation<
  void,
  VerificationEvidenceEnvQuery,
  VerificationCapabilityRegistryManageBody,
  VerificationCapabilityRegistryManageResponse
> = {
  method: "POST",
  path: () => "/api/verification/capability-registry/management",
  query: verificationEvidenceEnvQuery,
  body: verificationCapabilityRegistryManageBody,
  response: verificationCapabilityRegistryManageResponse,
  invalidates: [capabilityRegistryManagementListOp, connectorsCatalogOp],
};

export const verificationEvidence = {
  artifactsList: artifactsListOp,
  artifactsGet: artifactsGetOp,
  lineageGet: lineageGetOp,
  artifactsRedact: artifactsRedactOp,
  artifactsQueue: artifactsQueueOp,
  connectorsCatalog: connectorsCatalogOp,
  capabilityRegistryManagementList: capabilityRegistryManagementListOp,
  capabilityRegistryManage: capabilityRegistryManageOp,
  sourceIntentGet: sourceIntentGetOp,
  sourceIntentSession: sourceIntentSessionOp,
  sourceIntentLibrary: sourceIntentLibraryOp,
  sourceIntentSources: sourceIntentSourcesOp,
  sourceIntentGrant: sourceIntentGrantOp,
  sourceIntentIntake: sourceIntentIntakeOp,
  sourceIntentCompile: sourceIntentCompileOp,
  sourceIntentCompileCancel: sourceIntentCompileCancelOp,
  sourceIntentCompileStatus: sourceIntentCompileStatusOp,
  sourceIntentCompilationGet: sourceIntentCompilationGetOp,
  sourceIntentReview: sourceIntentReviewOp,
  sourceIntentReviewGet: sourceIntentReviewGetOp,
  connectorsList: connectorsListOp,
  connectorsGet: connectorsGetOp,
  credentialsGet: credentialsGetOp,
  connectorsReconnect: connectorsReconnectOp,
};
